import { api, throwApiError } from "../../../api/api";
import type {
  DpAllowanceBase,
  DpContractChangeReason,
  DpContractType,
  DpSalaryBasis,
} from "./dpTypes";

export type DpContractVersion = {
  id: string;
  contractId: string;
  effectiveFrom: string;
  /** Calculado na leitura pelo backend (véspera da vigência seguinte); `null` na versão atual. */
  effectiveTo: string | null;
  changeReason: DpContractChangeReason;
  positionId: string;
  position: { id: string; name: string; cbo?: string | null };
  cbo?: string | null;
  /** Prisma serializa Decimal como STRING no JSON — converta antes de formatar. */
  salaryAmount: string;
  salaryBasis: DpSalaryBasis;
  weeklyHours: string;
  monthlyHours: string;
  costCenterId: string;
  costCenter: { id: string; code: string; name: string };
  storeId?: string | null;
  store?: { id: string; nomeFantasia: string } | null;
  /** Adicionais. `null` = não informado (versões anteriores à regra); "0" = não recebe. */
  insalubrityPercent: string | null;
  insalubrityBase: DpAllowanceBase | null;
  insalubrityBaseAmount: string | null;
  hazardPercent: string | null;
  hazardBase: DpAllowanceBase | null;
  hazardBaseAmount: string | null;
  /** Atividade complementar: % sobre o salário contratual (base fixa). */
  complementaryActivityPercent: string | null;
  notes?: string | null;
  createdAt: string;
};

/** Opções de benefício por vigência (imutáveis). Decimais chegam como string. */
export type DpBenefitElection = {
  id: string;
  contractId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  salaryAdvanceOptIn: boolean;
  salaryAdvancePercent: string | null;
  transportVoucherOptIn: boolean;
  transportVoucherDailyAmount: string | null;
  transportVoucherEmployeePct: string | null;
  mealVoucherOptIn: boolean;
  mealVoucherDailyAmount: string | null;
  mealVoucherEmployeePct: string | null;
  /** VA (F1). `null` = não informado (eleições anteriores ao VA). */
  foodVoucherOptIn: boolean | null;
  foodVoucherAmount: string | null;
  foodVoucherMode: "POR_DIA" | "MENSAL" | null;
  foodVoucherEmployeePct: string | null;
  /** Aluguel de moto e vale-combustível (20/10/2026). `null` = não informado. */
  motorcycleRentalOptIn?: boolean | null;
  motorcycleRentalAmount?: string | null;
  fuelVoucherOptIn?: boolean | null;
  fuelVoucherAmount?: string | null;
  fuelVoucherMode?: "POR_DIA" | "MENSAL" | null;
  notes?: string | null;
  createdAt: string;
};

export type BenefitElectionPayload = {
  effectiveFrom: string;
  salaryAdvanceOptIn: boolean;
  salaryAdvancePercent?: number;
  transportVoucherOptIn: boolean;
  transportVoucherDailyAmount?: number;
  transportVoucherEmployeePct?: number;
  mealVoucherOptIn: boolean;
  mealVoucherDailyAmount?: number;
  mealVoucherEmployeePct?: number;
  foodVoucherOptIn?: boolean;
  foodVoucherAmount?: number;
  foodVoucherMode?: "POR_DIA" | "MENSAL";
  foodVoucherEmployeePct?: number;
  motorcycleRentalOptIn?: boolean;
  motorcycleRentalAmount?: number;
  fuelVoucherOptIn?: boolean;
  fuelVoucherAmount?: number;
  fuelVoucherMode?: "POR_DIA" | "MENSAL";
  notes?: string;
};

export type DpContractTermination = {
  id: string;
  contractId: string;
  terminationType: string;
  noticeType: string;
  lastWorkingDay: string;
  terminationDate: string;
  reason?: string | null;
};

export type DpEmployeeContract = {
  id: string;
  employeeId: string;
  employerId: string;
  employer: { id: string; legalName: string; tradeName?: string | null; cnpj: string };
  contractType: DpContractType;
  admissionDate: string;
  probationFirstEndDate?: string | null;
  probationEndDate?: string | null;
  fixedTermEndDate?: string | null;
  registrationNumber?: string | null;
  notes?: string | null;
  versions: DpContractVersion[];
  currentVersion: DpContractVersion | null;
  /** Presente na ficha do colaborador (GET /dp/employees/:id). */
  benefitElections?: DpBenefitElection[];
  currentBenefitElection?: DpBenefitElection | null;
  termination?: DpContractTermination | null;
};

export type ContractVersionPayload = {
  effectiveFrom: string;
  changeReason: DpContractChangeReason;
  positionId: string;
  cbo?: string;
  salaryAmount: number;
  salaryBasis: DpSalaryBasis;
  weeklyHours: number;
  monthlyHours: number;
  costCenterId: string;
  storeId?: string;
  insalubrityPercent?: number;
  insalubrityBase?: DpAllowanceBase;
  insalubrityBaseAmount?: number;
  hazardPercent?: number;
  hazardBase?: DpAllowanceBase;
  hazardBaseAmount?: number;
  complementaryActivityPercent?: number;
  notes?: string;
};

export type CreateContractPayload = {
  employerId: string;
  contractType: DpContractType;
  admissionDate: string;
  probationFirstEndDate?: string;
  probationEndDate?: string;
  fixedTermEndDate?: string;
  registrationNumber?: string;
  notes?: string;
  initialVersion: ContractVersionPayload;
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

export const listEmployeeContractsApi = async (
  token: string,
  employeeId: string
): Promise<DpEmployeeContract[]> => {
  try {
    const { data } = await api.get<DpEmployeeContract[]>(
      `/dp/employees/${employeeId}/contracts`,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar os contratos.");
    throw error;
  }
};

export const createContractApi = async (
  token: string,
  employeeId: string,
  payload: CreateContractPayload
): Promise<DpEmployeeContract> => {
  try {
    const { data } = await api.post<DpEmployeeContract>(
      `/dp/employees/${employeeId}/contracts`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao registrar a admissão.");
    throw error;
  }
};

export const addContractVersionApi = async (
  token: string,
  contractId: string,
  payload: ContractVersionPayload
): Promise<DpEmployeeContract> => {
  try {
    const { data } = await api.post<DpEmployeeContract>(
      `/dp/contracts/${contractId}/versions`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao registrar a alteração contratual.");
    throw error;
  }
};

/** Correção de uma versão (ADR 0030): o rastro guarda a versão como estava antes. */
export type DpContractVersionChange = {
  id: string;
  contractId: string;
  versionId: string;
  action: "EDITADA" | "EXCLUIDA";
  /** A versão inteira antes da mudança, como JSON (decimais em string, datas em ISO). */
  previousSnapshot: Partial<DpContractVersion> & { effectiveFrom?: string; salaryAmount?: string };
  reason: string;
  createdAt: string;
  createdBy: { id: string; nomeCompleto: string };
};

export const updateContractVersionApi = async (
  token: string,
  contractId: string,
  versionId: string,
  payload: ContractVersionPayload & { reason: string }
): Promise<DpEmployeeContract> => {
  try {
    const { data } = await api.put<DpEmployeeContract>(
      `/dp/contracts/${contractId}/versions/${versionId}`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao corrigir a versão contratual.");
    throw error;
  }
};

export const deleteContractVersionApi = async (
  token: string,
  contractId: string,
  versionId: string,
  reason: string
): Promise<DpEmployeeContract> => {
  try {
    const { data } = await api.delete<DpEmployeeContract>(
      `/dp/contracts/${contractId}/versions/${versionId}`,
      { ...auth(token), data: { reason } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao excluir a versão contratual.");
    throw error;
  }
};

export const listContractVersionChangesApi = async (
  token: string,
  contractId: string
): Promise<DpContractVersionChange[]> => {
  try {
    const { data } = await api.get<DpContractVersionChange[]>(
      `/dp/contracts/${contractId}/versions/changes`,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o histórico de correções.");
    throw error;
  }
};

export const getContractVersionAtApi = async (
  token: string,
  contractId: string,
  at: string
): Promise<DpContractVersion> => {
  try {
    const { data } = await api.get<DpContractVersion>(
      `/dp/contracts/${contractId}/versions`,
      { ...auth(token), params: { at } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao consultar a versão vigente na data.");
    throw error;
  }
};

// ── Opções de benefício ─────────────────────────────────────────────────────

export const createBenefitElectionApi = async (
  token: string,
  contractId: string,
  payload: BenefitElectionPayload
): Promise<DpBenefitElection[]> => {
  try {
    const { data } = await api.post<DpBenefitElection[]>(
      `/dp/contracts/${contractId}/benefit-elections`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao registrar as opções de benefício.");
    throw error;
  }
};

/** Correção de um registro lançado errado (ADR 0026): substitui conteúdo e vigência. */
export const updateBenefitElectionApi = async (
  token: string,
  contractId: string,
  electionId: string,
  payload: BenefitElectionPayload
): Promise<DpBenefitElection[]> => {
  try {
    const { data } = await api.put<DpBenefitElection[]>(
      `/dp/contracts/${contractId}/benefit-elections/${electionId}`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao salvar as opções de benefício.");
    throw error;
  }
};

export const deleteBenefitElectionApi = async (
  token: string,
  contractId: string,
  electionId: string
): Promise<DpBenefitElection[]> => {
  try {
    const { data } = await api.delete<DpBenefitElection[]>(
      `/dp/contracts/${contractId}/benefit-elections/${electionId}`,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao excluir as opções de benefício.");
    throw error;
  }
};

// ── Contrato de experiência ───────────────────────────────────────────────────

export type UpdateProbationPayload = {
  probationFirstEndDate: string | null;
  probationEndDate: string | null;
  reason: string;
};

export type DpProbationChange = {
  id: string;
  contractId: string;
  previousFirstEndDate: string | null;
  previousEndDate: string | null;
  newFirstEndDate: string | null;
  newEndDate: string | null;
  reason: string;
  createdAt: string;
  createdBy: { id: string; nomeCompleto: string };
};

export type DpProbationMilestone = "PRIMEIRO_PERIODO" | "FIM";

export type DpExpiringProbation = {
  contractId: string;
  employee: { id: string; fullName: string };
  registrationNumber: string | null;
  store: { id: string; nomeFantasia: string } | null;
  admissionDate: string;
  probationFirstEndDate: string | null;
  probationEndDate: string | null;
  milestone: DpProbationMilestone;
  date: string;
  daysLeft: number;
  terminationProcessId: string | null;
};

export const PROBATION_MILESTONE_LABELS: Record<DpProbationMilestone, string> = {
  PRIMEIRO_PERIODO: "Fim do 1º período",
  FIM: "Fim da experiência",
};

export const updateProbationApi = async (
  token: string,
  contractId: string,
  payload: UpdateProbationPayload
): Promise<{ id: string; probationFirstEndDate: string | null; probationEndDate: string | null }> => {
  try {
    const { data } = await api.patch(`/dp/contracts/${contractId}/probation`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao alterar a experiência.");
    throw error;
  }
};

export const listProbationChangesApi = async (token: string, contractId: string): Promise<DpProbationChange[]> => {
  try {
    const { data } = await api.get<DpProbationChange[]>(`/dp/contracts/${contractId}/probation/changes`, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o histórico da experiência.");
    throw error;
  }
};

export const listExpiringProbationsApi = async (
  token: string
): Promise<{ days: number; items: DpExpiringProbation[] }> => {
  try {
    const { data } = await api.get<{ days: number; items: DpExpiringProbation[] }>(
      "/dp/contracts/probation/expiring",
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar as experiências.");
    throw error;
  }
};

export const getProbationSummaryApi = async (
  token: string
): Promise<{ days: number; firstPeriodEnding: number; ending: number }> => {
  try {
    const { data } = await api.get<{ days: number; firstPeriodEnding: number; ending: number }>(
      "/dp/contracts/probation/summary",
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o resumo das experiências.");
    throw error;
  }
};
